import { Box, Portal } from '@mui/material';
import { ComponentType, ReactNode, useCallback, useMemo, useState } from 'react';
import { Rnd } from 'react-rnd';

interface ResizableDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback fired when the dialog is closed
   */
  onClose?: () => void;
  /**
   * Title displayed in the toolbar
   */
  title: string;
  /**
   * Content to display inside the dialog
   */
  children: ReactNode;
  /**
   * Initial size of the dialog
   */
  initialSize?: { width: number; height: number };
  /**
   * Aspect ratio to maintain during resize
   */
  aspectRatio?: number;
  /**
   * CSS class name for the drag handle
   */
  dragHandleClassName?: string;
  /**
   * CSS selector for elements that should cancel drag operations
   */
  cancelSelector?: string;
  /**
   * Minimum width of the dialog
   */
  minWidth?: number;
  /**
   * Maximum width of the dialog
   */
  maxWidth?: number;
  /**
   * Component to use as the container inside the Rnd wrapper.
   * Defaults to Box if not provided.
   */
  component?: ComponentType<any>;
  /**
   * Additional props to pass to the component
   */
  componentProps?: Record<string, any>;
  /**
   * When true, the dialog is kept mounted but hidden (opacity 0, no pointer events).
   * Used e.g. when content is shown in Picture-in-Picture and the source must stay in DOM.
   */
  hidden?: boolean;
}

/**
 * Resizable and draggable dialog component using react-rnd.
 * The dialog can be dragged by the toolbar area and resized by the bottom-right corner.
 * Maintains aspect ratio during resize if aspectRatio is provided.
 * Non-modal dialog that allows interaction with the rest of the page.
 */
export function ResizableDialog({
  open,
  onClose,
  title,
  children,
  initialSize = { width: 400, height: 300 },
  aspectRatio,
  dragHandleClassName = 'drag-handle',
  cancelSelector,
  minWidth = 300,
  maxWidth = 1200,
  hidden = false,
  // component: Component = Box,
  // componentProps = {},
}: ResizableDialogProps) {
  const [size, setSize] = useState(initialSize);
  const [position, setPosition] = useState(() => {
    // Calculate initial position to be bottom-right of viewport
    if (typeof window !== 'undefined') {
      return {
        x: window.innerWidth - initialSize.width - 24,
        y: window.innerHeight - initialSize.height - 24,
      };
    }
    return { x: 0, y: 0 };
  });

  // Calculate aspect ratio from initial size if not provided
  const calculatedAspectRatio = useMemo(() => {
    if (aspectRatio !== undefined) {
      return aspectRatio;
    }
    return initialSize.width / initialSize.height;
  }, [aspectRatio, initialSize.width, initialSize.height]);

  const handleResizeStop = useCallback(
    (
      e: MouseEvent | TouchEvent,
      direction: string,
      ref: HTMLElement,
      delta: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      const newWidth = ref.offsetWidth;
      const newHeight = calculatedAspectRatio ? newWidth / calculatedAspectRatio : ref.offsetHeight;
      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: position.x, y: position.y });
    },
    [calculatedAspectRatio],
  );

  const handleDragStop = useCallback((e: any, d: { x: number; y: number }) => {
    setPosition({ x: d.x, y: d.y });
  }, []);

  if (!open) return null;

  return (
    <Portal>
      <Rnd
        size={size}
        position={position}
        onDragStop={handleDragStop}
        onResizeStop={handleResizeStop}
        lockAspectRatio={calculatedAspectRatio}
        minWidth={minWidth}
        minHeight={calculatedAspectRatio ? minWidth / calculatedAspectRatio : minWidth}
        maxWidth={maxWidth}
        maxHeight={calculatedAspectRatio ? maxWidth / calculatedAspectRatio : maxWidth}
        bounds="window"
        dragHandleClassName={dragHandleClassName}
        cancel={cancelSelector}
        enableResizing={{
          topRight: true,
          topLeft: true,
        }}
        style={{
          position: 'fixed',
          zIndex: 1300, // Same as MUI Dialog z-index
          ...(hidden && {
            opacity: 0,
            pointerEvents: 'none',
          }),
        }}
      >
        <Box
          role="dialog"
          aria-modal="false"
          aria-label={title}
          tabIndex={-1}
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 48px)',
            maxWidth: 'calc(100vw - 48px)',
          }}
        >
          {children}
        </Box>
      </Rnd>
    </Portal>
  );
}
